// Preloader — CWV: never pull AuthModal / Contact / About on SEO landings
export class PagePreloader {
  private static preloadedPages = new Set<string>();

  private static isSeoLanding(): boolean {
    if (typeof window === 'undefined') return false;
    return /\/(compare|use-case|replace)(\/|$)/i.test(window.location.pathname);
  }

  // Preload une page spécifique
  static async preloadPage(pageName: string): Promise<void> {
    if (this.preloadedPages.has(pageName)) {
      return;
    }

    try {
      switch (pageName) {
        case 'pricing':
          await import('../pages/Pricing');
          break;
        case 'contact':
          await import('../pages/Contact');
          break;
        case 'about':
          await import('../pages/About');
          break;
        case 'compare':
          await import('../pages/Compare');
          break;
        case 'blog':
          await import('../pages/Blog');
          break;
        case 'support':
          await import('../pages/SupportCenter');
          break;
        default:
          return;
      }

      this.preloadedPages.add(pageName);
    } catch (error) {
      console.warn(`⚠️ Erreur preload ${pageName}:`, error);
    }
  }

  /**
   * Priority preload — skipped entirely on compare/use-case/replace landings.
   * Elsewhere: very late idle (hover nav is preferred via preloadRelatedPages).
   */
  static initPreloader(): void {
    if (this.isSeoLanding()) {
      return;
    }

    const run = () => {
      // Soft list — contact only, no About/Compare (AuthModal chain)
      ['contact'].forEach((page) => {
        this.preloadPage(page);
      });
    };
    const schedule = () => {
      const w = window as Window & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      };
      if (typeof w.requestIdleCallback === 'function') {
        w.requestIdleCallback(run, { timeout: 25000 });
      } else {
        setTimeout(run, 20000);
      }
    };
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', () => setTimeout(schedule, 8000));
  }

  /** Related preload — disabled on SEO landings (PSI unused JS / AuthModal). */
  static preloadRelatedPages(currentPage: string): void {
    if (this.isSeoLanding()) {
      return;
    }
    if (
      currentPage === 'compare-landing' ||
      currentPage === 'use-case' ||
      currentPage === 'replace-landing' ||
      currentPage === 'compare'
    ) {
      return;
    }

    const relatedPages: Record<string, string[]> = {
      home: [],
      pricing: ['contact'],
      contact: ['support'],
      about: ['contact'],
    };

    const related = relatedPages[currentPage] || [];
    related.forEach((page) => {
      setTimeout(() => this.preloadPage(page), 10000);
    });
  }

  /** Call from nav hover / focus to warm a route without hurting LCP. */
  static preloadOnIntent(pageName: string): void {
    void this.preloadPage(pageName);
  }
}
